<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-cart-arrow-down"></i> {{ _('Shared orders') }}</h3>
        </div>
      </div>
      <div class="list-filter-row">
        <div class="list-filter-col" style="max-width: 300px">
          <div class="form-group">
            <div class="input-group">
              <input class="form-control" id="searchField" type="text" v-model="search" :placeholder="_('Keyword search')" />
              <span class="input-group-btn">
                <button type="button" class="btn btn-success"><i class="fa fa-fw fa-search"></i></button>
              </span>
            </div>
          </div>
        </div>
        <div class="list-filter-col" style="max-width: 300px">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Display') }}</div>
              <select class="form-control" id="orderTypeFilter" v-model="type">
                <option value="all">{{ _('Select...') }}</option>
                <option value="all">{{ _('All shared orders') }}</option>
                <option value="external">{{ _('External orders') }}</option>
                <option value="local">{{ _('Local orders') }}</option>
                <optgroup :label="_('Shops')">
                  <option v-for="shop in shops" :value="shop">{{ shop }}</option>
                </optgroup>
                <option disabled="disabled">────────────────────</option>
                <option value="orders">{{ _('All orders') }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="list-filter-col" v-if="!showExactRange" style="max-width: 300px">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Filter') }}</div>
              <select class="form-control" id="orderRangeFilter" v-model="range">
                <option class="rangeOption" value="all">{{ _('No filter') }}</option>
                <option class="rangeOption" value="lastweek">{{ _('Last week') }}</option>
                <option class="rangeOption" value="lastmonth">{{ _('Last month') }}</option>
                <option class="rangeOption" value="lastyear">{{ _('Last year') }}</option>
                <option class="rangeOption" value="all">{{ _('All orders') }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="list-filter-col" v-if="showExactRange" style="max-width: 500px">
          <div class="rangeFields pull-right">
            <div class="form-inline">
              <div class="form-group">
                <DatePicker v-model="dateStart" :max-date="dateEnd" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
              </div>
              <label for="endDate"> - </label>
              <div class="form-group">
                <DatePicker v-model="dateEnd" :min-date="dateStart" :shortLocale="shortLocale" :staticInput="true"></DatePicker>
              </div>
            </div>
          </div>
        </div>
        <div class="list-filter-col button-col">
    			<button id="selectRange" type="button" class="btn btn-default" v-on:click="selectRange" v-if="!showExactRange"><div class=""></div>{{ _('Select exact date range') }}</button>
    			<button id="unselectRange" type="button" class="btn btn-default" v-on:click="unselectRange" v-else><div class=""></div>{{ _('Cancel') }}</button>
        </div>
        <div class="button-col">
          <button class="btn btn-success btn-download" type="button" v-on:click="downloadXlsx"><i class="fa fa-fw fa-download"></i><span class="hidden-xs">{{ _('Download Excel file') }}</span></button>
        </div>
      </div>
      <Datatable :url="consolidatedOrdersUrl" :search="search" :id-filter="type" :order-date-filter="dateRangeFilter" :options="tableOptions" ref="datatable" v-on:data="tableEvent" />
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import formatters from '~/lib/formatters'
import Datatable from '~/components/Datatable'
import debounce from 'lodash/debounce'

export default {
  data () {
    let $this = this
    const _ = useNuxtApp().$translate
    return {
      orders: [],
      shops: [],
      showExactRange: false,
      dateStart: false,
      dateEnd: false,
      search: '',
      type: 'all',
      range: 'all',
      tableOptions: {
        selectable: false,
        responsiveLayoutCollapseFormatter: this.collapseFormatter(),
        columns: [
        {
          title: _('Order no.'),
          field: 'id',
          width: 110,
          responsive: 0
        },
        {
          title: _('Total (vat 0%)'),
          field: 'totalVatFree',
          widthGrow: 2,
          formatter: this.numberFormatter(),
          hozAlign: 'right'
        },
        {
          title: _('Total (inc. vat)'),
          field: 'total',
          widthGrow: 2,
          formatter: this.numberFormatter(),
          hozAlign: 'right'
        },
        {
          title: _('Order date'),
          field: 'orderdate',
          formatter: this.dateAndTimeFormatter(),
          widthGrow: 2
        },
        {
          title: _('Shops'),
          field: 'shop_ids',
          widthGrow: 2,
          formatter: this.shopIdListFormatter()
        },
        ],
        initialSort: [{
          column: 'id',
          dir: 'desc'
        }],
        rowClick (e, row) {
          $this.editRow(row)
        }
      }
    }
  },
  mixins: [components, formatters],
  computed: {
    consolidatedOrdersUrl () {
      return this.baseDomain + this.shopId + '/consolidatedorders.json?apikey=' + this.apiKey + '&rawformat=true'
    },
    dateRangeFilter: {
      get () {
        if (this.dateStart && this.dateEnd) {
          return DateTime.fromJSDate(this.dateStart, { zone: this.timezone }).toFormat('d.M.yyyy') + '-' + DateTime.fromJSDate(this.dateEnd, { zone: this.timezone }).toFormat('d.M.yyyy')
        }
        return this.dateRange
      },
      set (newValue) {
        if (!newValue || !newValue.match || !newValue.match('-')) {
          this.showExactRange = false
          this.dateStart = false
          this.dateEnd = false
          this.dateRange = newValue
          return
        }
        this.setExactRangeFromFilter(newValue)
      }
    },
    dateRange: {
      get () {
        switch (this.range) {
          case 'lastweek':
          case 'lastmonth':
          case 'lastyear':
          case 'exact':
            return this.range
        }
        return ''
      },
      set (newValue) {
        switch (newValue) {
          case 'lastweek':
          case 'lastmonth':
          case 'lastyear':
          case 'exact':
            this.range = newValue
        }
      }
    }
  },
  methods: {
    downloadXlsx () {
      const filters = []
      if (this.search) {
        filters.push({ field: 'search', type: 'like', value: this.search })
      }
      if (this.type) {
        filters.push({ field: 'type', type: '=', value: this.type })
      }
      if (this.tag) {
        filters.push({ field: 'tag', type: '=', value: this.tag })
      }
      if (this.pos) {
        filters.push({ field: 'pos_name', type: '=', value: this.pos })
      }
      if (this.dateRangeFilter) {
        filters.push({ field: 'orderdate', type: '=', value: this.dateRangeFilter })
      }
      let filterStr = ''
      if (filters.length) {
        filterStr = '&filters[]=' + filters.map(f => JSON.stringify(f)).join('&filters[]=')
      }
      window.open(this.baseDomain + this.shopId + '/consolidatedorders.xlsx?apikey=' + this.apiKey + filterStr)
    },
    editRow (row) {
      const id = row.getCell('id').getValue()
      this.editOrder(id)
    },
    editOrder (id, skipState) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'ConsolidatedOrderDetails',
        title: this._('Shared order') + ' ' + id,
        width: 6,
        data: {
          orderId: id
        },
        skipState
      })
    },
    reload () {
      if (this.$refs && this.$refs.datatable) {
        this.$refs.datatable.reload()
      }
    },
    selectRange () {
      this.showExactRange = true
    },
    unselectRange () {
      this.showExactRange = this.dateStart = this.dateEnd = false
    },
    loadShops () {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/consolidatedorders.json?shops=true&apikey=' + this.apiKey)
        } catch (e) {
          resolve([])
          return
        }
        resolve(response.data)
      })
    }
  },
  components: {
    Datatable
  },
  watch: {
    type (newValue, oldValue) {
      if (newValue === 'orders') {
        this.EventBus.$emit('showComponent', {
          shopId: this.shopId,
          component: 'OrderList',
          title: this._('Orders')
        })
        this.type = oldValue
      }
    }
  },
  async mounted () {
    this.shops = await this.loadShops()
    if(!this.events) {
      let events = {}
      events['restoreDetails'] = (message) => {
        if (message.shopId !== this.shopId) {
          return
        }
        switch(message.component) {
          case 'ConsolidatedOrderDetails':
            this.editOrder(message.data.orderId, null, true)
            break
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>